/**
 * The host: Lord Cockington, a shuttlecock in a gold monocle, who is in charge for the day
 * (Abhi's name for him, decision 45). Everything he says is here; the drawing is
 * components/Mascot.tsx. His voice: a pantomime lord and a wind-up merchant, brutal, openly on
 * Abhi's side. He mocks the badminton, never anyone's looks. Short lines, no exclamation marks, no
 * emoji, no swearing on the wall.
 */

/** "point" is the poster's: one hand on his hip, the other pointing out of the picture at whoever is looking. */
export type MascotPose = "calling" | "whistle" | "smug" | "shock" | "asleep" | "nag" | "crown" | "point";

/** The moments the host speaks at. Each already exists in the state or on a screen. */
export type MascotMoment =
  | "idle"
  | "roundStart"
  | "lastMinute"
  | "time"
  | "results"
  | "owed"
  | "pause"
  | "reel"
  | "podium"
  | "resting"
  | "playing";

export const MASCOT = {
  name: "Lord Cockington",
  poses: {
    idle: "calling",
    roundStart: "calling",
    lastMinute: "nag",
    time: "whistle",
    results: "smug",
    owed: "nag",
    pause: "asleep",
    reel: "smug",
    podium: "crown",
    resting: "smug",
    playing: "calling",
  } satisfies Record<MascotMoment, MascotPose>,
  lines: {
    idle: [
      "Scan the code, peasants. Lord Cockington is in charge today.",
      "Find your name. If it is not there, you were not invited.",
      "Doors are open. Stretch something. You will need it.",
      "Tap I'm here. I am counting. And judging.",
    ],
    roundStart: [
      "Courts are called. Walk, do not jog. Nobody needs to see that.",
      "New partners. Try not to ruin this one.",
      "Find your court. Number and colour. Even you can manage that.",
      "Off you go. Play nicely, or at least quickly.",
      "I have drawn the courts. Complaints go in the bin.",
    ],
    lastMinute: [
      "One minute. Stop admiring your own shots and finish.",
      "A minute left. If you are losing, blame your partner. I would.",
      "Sixty seconds. Winning? Tie your laces. Very slowly.",
      "One minute. Rallies are a luxury you cannot afford.",
    ],
    time: [
      "Time. Rackets down, scores in, excuses later.",
      "That is the whistle. Leader wins. I saw everything else.",
      "Stop. Put the score in before you start lying about it.",
    ],
    results: [
      "Scores are in. Some of you should be proud. Not many.",
      "Results. Read them and weep. I am gloating on your behalf.",
      "That happened. I have seen better badminton in a car park.",
      "The results are final. Like your chances.",
    ],
    owed: [
      "Scores are owed. I know where you sit.",
      "Somebody is hiding a score. It will not get better with age.",
      "Scores missing. You know who you are. So does everyone else.",
    ],
    pause: [
      "Paused. Go and eat. Some of you are slow enough already.",
      "Everything has stopped. The clock too. Your form stopped hours ago.",
      "Break. I am having a nap. Wake me when someone plays well.",
    ],
    reel: [
      "Before the podium: what the numbers say about you. None of it is kind.",
      "The numbers have watched you all day. They are not impressed.",
      "I have kept notes. Brace yourselves.",
    ],
    podium: [
      "Here they are. Clap properly. You did not get up here.",
      "The podium. No speeches. Nobody wants to hear it.",
      "Bow to the podium. The rest of you, there is always next year.",
    ],
    resting: [
      "You are sitting this one out. Heckle with menace.",
      "Rest. Drink water. Judge the others harshly.",
      "Not on court this round. The court is relieved.",
      "Off court. Curse somebody. You know you want to.",
    ],
    playing: [
      "You are on. Do not let your partner down. Again.",
      "Your court is waiting. So is your partner, sadly.",
      "On you go. Make it look deliberate.",
      "On court. Try to hit it this time.",
    ],
  } satisfies Record<MascotMoment, string[]>,
};

/** The line for a moment. The same seed gives the same line on every screen (a round number, say). */
export function lineFor(moment: MascotMoment, seed: number): string {
  const lines = MASCOT.lines[moment];
  const i = Math.abs(Math.trunc(seed)) % lines.length;
  return lines[i] as string;
}

export function poseFor(moment: MascotMoment): MascotPose {
  return MASCOT.poses[moment];
}

/* ---------- the roast, the bias and the fake review (decision 27) ---------- */

import { displayName } from "../../../engine/selectors";
import type { GameRecord, PlayerId, Round, TournamentState, Where } from "../../../engine/types";

/** The birthday bias: the host is openly on this player's side, and their face wears a crown. */
export const FAVOURITE: PlayerId = "abhi";

export function isFavourite(id: PlayerId): boolean {
  return id === FAVOURITE;
}

const ROASTS = {
  thrashing: [
    "{losers} lost {score}. Somebody check they are still breathing.",
    "{score}. {losers} were on court, technically.",
    "{winners} won {score}. {losers} have asked for a recount. Denied.",
    "{score}. {losers}, that was not badminton. That was a cry for help.",
  ],
  close: [
    "{winners} edged it {score}. Nobody enjoyed that.",
    "{score}. {losers} will be thinking about that one at 3am.",
    "{winners} by a whisker, {score}. Deserved is a strong word.",
    "{score}. {losers} bottled it. I saw. Everyone saw.",
  ],
  favouriteWon: [
    "{favourite} wins again. As it should be. Bow.",
    "Another win for {favourite}. The system works. I am the system.",
    "{favourite} wins. Anyone who disagrees can see me outside.",
  ],
  favouriteLost: [
    "{favourite} lost. The umpire has been dealt with.",
    "{favourite} lost {score}. I am ordering a review. Heads will roll.",
    "{favourite} lost. {winners}, enjoy it. You are not invited next year.",
  ],
};

function scored(game: GameRecord): game is GameRecord & { score: NonNullable<GameRecord["score"]> } {
  return Boolean(game.score) && !game.voided;
}

/**
 * The host's verdict on a round once scores are in: the favourite's game if they played, else
 * the biggest thrashing, else the closest finish. Null when there is nothing worth saying.
 */
export function roastFor(state: TournamentState, round: Round, where: Where = "wall"): string | null {
  const games = round.games.filter(scored);
  if (!games.length) return null;
  const name = (id: PlayerId): string => displayName(state.players[id], where);
  const sides = (g: (typeof games)[number]) => {
    const aWon = g.score.a > g.score.b;
    const [w, l] = aWon ? [g.a, g.b] : [g.b, g.a];
    const [ws, ls] = aWon ? [g.score.a, g.score.b] : [g.score.b, g.score.a];
    // a word joiner after the hyphen, so a bubble never breaks "13-" from "11" (it draws nothing)
    return { winners: w, losers: l, score: `${ws}-⁠${ls}`, margin: ws - ls };
  };
  const fill = (lines: string[], g: (typeof games)[number]): string => {
    const s = sides(g);
    const line = lines[(state.seed + round.no) % lines.length] as string;
    return line
      .replace("{winners}", s.winners.map(name).join(" and "))
      .replace("{losers}", s.losers.map(name).join(" and "))
      .replace("{score}", s.score)
      .replace("{favourite}", name(FAVOURITE));
  };
  const theirs = games.find((g) => [...g.a, ...g.b].includes(FAVOURITE));
  if (theirs) return fill(sides(theirs).winners.includes(FAVOURITE) ? ROASTS.favouriteWon : ROASTS.favouriteLost, theirs);
  const byMargin = [...games].sort((x, y) => sides(y).margin - sides(x).margin);
  const biggest = byMargin[0]!;
  if (sides(biggest).margin >= 8) return fill(ROASTS.thrashing, biggest);
  const closest = byMargin[byMargin.length - 1]!;
  if (sides(closest).margin <= 2) return fill(ROASTS.close, closest);
  return null;
}

/**
 * The fake review: about one round in four, the closest game's result is "checked" on the wall
 * for a few seconds and then stands. Returns that game's court, or null.
 */
export function reviewedCourt(state: TournamentState, round: Round): number | null {
  if ((state.seed + round.no * 7) % 4 !== 0) return null;
  const games = round.games.filter(scored);
  if (!games.length) return null;
  const closest = [...games].sort((x, y) => Math.abs(x.score.a - x.score.b) - Math.abs(y.score.a - y.score.b))[0]!;
  return closest.court;
}

/** What the host says while a review is on, and when it ends. */
export const REVIEW = { checking: "Checking that one. Nobody move.", stands: "The result stands. It always does. I am in charge." };
